import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import { getResourceById } from '../services/resourceService';

// Simple Markdown renderer component
const MarkdownRenderer = ({ content }) => {
  const renderMarkdown = (text) => {
    if (!text) return null;
    
    // Split into lines for block-level processing
    const lines = text.split('\n');
    const elements = [];
    let listItems = [];
    let listType = null; // 'ul' or 'ol'
    let key = 0;
    
    const flushList = () => {
      if (listItems.length > 0) {
        if (listType === 'ol') {
          elements.push(
            <ol key={key++} className="list-decimal list-inside mb-4 space-y-1 ml-4">
              {listItems}
            </ol>
          );
        } else {
          elements.push(
            <ul key={key++} className="list-disc list-inside mb-4 space-y-1 ml-4">
              {listItems}
            </ul>
          );
        }
        listItems = [];
        listType = null;
      }
    };
    
    const processInline = (line) => {
      // Process inline formatting: bold, italic, links
      const parts = [];
      let remaining = line;
      let partKey = 0;
      
      while (remaining.length > 0) {
        // Check for links [text](url)
        const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
        // Check for bold **text**
        const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
        // Check for italic *text* (but not **)
        const italicMatch = remaining.match(/(?<!\*)\*([^*]+)\*(?!\*)/);
        
        // Find the earliest match
        const matches = [
          linkMatch ? { type: 'link', match: linkMatch, index: remaining.indexOf(linkMatch[0]) } : null,
          boldMatch ? { type: 'bold', match: boldMatch, index: remaining.indexOf(boldMatch[0]) } : null,
          italicMatch ? { type: 'italic', match: italicMatch, index: remaining.indexOf(italicMatch[0]) } : null,
        ].filter(Boolean).sort((a, b) => a.index - b.index);
        
        if (matches.length === 0) {
          parts.push(remaining);
          break;
        }
        
        const first = matches[0];
        
        // Add text before the match
        if (first.index > 0) {
          parts.push(remaining.substring(0, first.index));
        }
        
        // Add the formatted element
        if (first.type === 'link') {
          parts.push(
            <a key={partKey++} href={first.match[2]} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-800 underline">
              {first.match[1]}
            </a>
          );
        } else if (first.type === 'bold') {
          parts.push(<strong key={partKey++} className="font-semibold">{first.match[1]}</strong>);
        } else if (first.type === 'italic') {
          parts.push(<em key={partKey++} className="italic">{first.match[1]}</em>);
        }
        
        remaining = remaining.substring(first.index + first.match[0].length);
      }
      
      return parts;
    };
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Image ![alt](src)
      const imageMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      if (imageMatch) {
        flushList();
        elements.push(
          <div key={key++} className="my-6">
            <img 
              src={imageMatch[2]} 
              alt={imageMatch[1] || 'Article image'} 
              className="max-w-full h-auto rounded-lg shadow-sm mx-auto"
            />
          </div>
        );
        continue;
      }
      
      // Horizontal rule
      if (line.trim() === '---' || line.trim() === '***') {
        flushList();
        elements.push(<hr key={key++} className="my-6 border-t border-gray-200" />);
        continue;
      }
      
      // H2 heading
      if (line.startsWith('## ')) {
        flushList();
        elements.push(
          <h2 key={key++} className="text-2xl font-semibold text-charcoal-800 mt-6 mb-3">
            {processInline(line.substring(3))}
          </h2>
        );
        continue;
      }
      
      // H3 heading
      if (line.startsWith('### ')) {
        flushList();
        elements.push(
          <h3 key={key++} className="text-xl font-semibold text-charcoal-800 mt-5 mb-2">
            {processInline(line.substring(4))}
          </h3>
        );
        continue;
      }
      
      // Bullet list item
      if (line.match(/^[-*]\s/)) {
        if (listType !== 'ul') {
          flushList();
          listType = 'ul';
        }
        listItems.push(
          <li key={key++} className="text-charcoal-700">
            {processInline(line.substring(2))}
          </li>
        );
        continue;
      }
      
      // Numbered list item
      if (line.match(/^\d+\.\s/)) {
        if (listType !== 'ol') {
          flushList();
          listType = 'ol';
        }
        const content = line.replace(/^\d+\.\s/, '');
        listItems.push(
          <li key={key++} className="text-charcoal-700">
            {processInline(content)}
          </li>
        );
        continue;
      }
      
      // Empty line
      if (line.trim() === '') {
        flushList();
        continue;
      }
      
      // Regular paragraph
      flushList();
      elements.push(
        <p key={key++} className="text-charcoal-700 mb-4 leading-relaxed">
          {processInline(line)}
        </p>
      );
    }
    
    flushList();
    return elements;
  };
  
  return <div className="prose-custom">{renderMarkdown(content)}</div>;
};

const ResourceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState(null);
  const [tried, setTried] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadResource = async () => {
      try {
        setLoading(true);
        const result = await getResourceById(id);
        if (result.success && result.data) {
          // Convert Supabase format to display format
          const formattedResource = {
            id: result.data.id,
            title: result.data.title,
            description: result.data.description,
            category: result.data.category,
            type: result.data.type,
            image: result.data.image_url,
            content: result.data.content,
            author: result.data.author,
            tags: result.data.tags || [],
            readTime: result.data.read_time,
            featured: result.data.featured,
            link: result.data.link
          };
          setResource(formattedResource);
        } else {
          setResource(null);
        }
      } catch (error) {
        console.error('Error loading resource:', error);
        setResource(null);
      } finally {
        setLoading(false);
      }
    };

    loadResource();
  }, [id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Determine if this is a recipe-type resource
  const isRecipe = useMemo(() => {
    const t = String(resource?.type || '').toLowerCase();
    const c = String(resource?.category || '').toLowerCase();
    return t.includes('recipe') || c.includes('recipe');
  }, [resource]);

  // Load tried state from localStorage
  useEffect(() => {
    try {
      const arr = JSON.parse(localStorage.getItem('recipeHistory') || '[]');
      if (Array.isArray(arr)) {
        setTried(arr.some(ev => String(ev.id) === String(id)));
      } else {
        setTried(false);
      }
    } catch {
      setTried(false);
    }
  }, [id]);

  const handleMarkTried = () => {
    try {
      const entry = {
        id,
        title: resource?.title || '',
        timestamp: Date.now()
      };
      const arr = (() => {
        try { const v = JSON.parse(localStorage.getItem('recipeHistory') || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
      })();
      // Avoid duplicates by id, keep the latest timestamp
      const filtered = arr.filter(ev => String(ev.id) !== String(id));
      const next = [...filtered, entry];
      localStorage.setItem('recipeHistory', JSON.stringify(next));
      setTried(true);
    } catch {}
  };

  const formattedTags = useMemo(() => {
    if (!resource) return [];
    if (Array.isArray(resource.tags)) return resource.tags;
    if (typeof resource.tags === 'string') return resource.tags.split(',').map(t => t.trim()).filter(Boolean);
    return [];
  }, [resource]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 pt-20 p-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-lg p-8 border">
            <p className="text-charcoal-600">Loading resource...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!resource) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 pt-20 p-6">
        <div className="max-w-3xl mx-auto">
          <button onClick={() => navigate(-1)} className="text-purple-700 hover:underline mb-6">← Back</button>
          <div className="bg-white rounded-lg p-8 border">
            <h1 className="text-2xl font-semibold text-charcoal-800 mb-2">Resource not found</h1>
            <p className="text-charcoal-600">The resource you are looking for may have been removed or is unavailable.</p>
          </div>
        </div>
      </div>
    );
  }

  const hasContent = Boolean(resource.content && String(resource.content).trim());

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Back Button */}
        <div className="mb-6">
          <RouterLink to="/resources" className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors min-h-[44px]"> 
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">Back to Wellness Hub</span>
          </RouterLink>
        </div>

        {/* Article Container */}
        <article className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Featured Image */}
          {resource.image && (
            <div className="relative w-full h-64 sm:h-80 md:h-96 bg-gradient-to-br from-purple-50 to-pink-50 overflow-hidden">
              {(resource.image.startsWith('data:') || resource.image.startsWith('http')) ? (
                <img 
                  src={resource.image} 
                  alt={resource.title} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-8xl sm:text-9xl">
                  {resource.image}
                </div>
              )}
            </div>
          )}

          {/* Content Area */}
          <div className="p-6 sm:p-8 md:p-10">
            {/* Tags & Meta */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {resource.type && (
                <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                  {resource.type}
                </span>
              )}
              {resource.category && (
                <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                  {resource.category}
                </span>
              )}
              {resource.readTime && (
                <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  ⏱️ {resource.readTime}
                </span>
              )}
              {isRecipe && (
                tried ? (
                  <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    Tried ✓
                  </span>
                ) : (
                  <button 
                    onClick={handleMarkTried} 
                    className="px-4 py-1.5 rounded-full text-xs font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
                  >
                    Mark as Tried
                  </button>
                )
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-charcoal-800 mb-4 leading-tight">
              {resource.title}
            </h1>

            {/* Author & Date Info */}
            {resource.author && (
              <div className="flex items-center text-sm text-charcoal-600 mb-6 pb-6 border-b">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="font-medium">{resource.author}</span>
              </div>
            )}

            {/* Description */}
            {resource.description && (
              <p className="text-lg sm:text-xl text-charcoal-700 leading-relaxed mb-8 font-light">
                {resource.description}
              </p>
            )}

            {/* Tags */}
            {formattedTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {formattedTags.map((tag, idx) => (
                  <span key={idx} className="px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Main Content */}
            {hasContent ? (
              <div 
                className="resource-content prose prose-lg max-w-none"
                style={{ lineHeight: '1.8' }}
                dangerouslySetInnerHTML={{ __html: resource.content }}
              />
            ) : resource.link ? (
              <div className="bg-purple-50 rounded-xl p-6 sm:p-8 text-center">
                <p className="text-charcoal-700 mb-6 text-lg">
                  This resource is available via an external link.
                </p>
                <a
                  href={resource.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium min-h-[44px]"
                >
                  Open Resource
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-6 sm:p-8 text-center">
                <p className="text-charcoal-600">No content or link has been provided for this resource yet.</p>
              </div>
            )}
          </div>
        </article>
      </div>
    </div>
  );
};

export default ResourceDetail;
